import React, { useState, useEffect } from 'react';
import './ProductsPage.css';
import HomeButton from './HomeButton';
import useAutoSave, { autoSaveLabel } from '../hooks/useAutoSave';
import api from '../services/api';
import ProductAssemblyForm from './ProductAssemblyForm';

const CHAT_API_BASE = '/api/chat';

// Main Products Page Component
const ProductsPage = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productImages, setProductImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Edit state
  const [editingProduct, setEditingProduct] = useState(null);
  const [moduleFields, setModuleFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);

  const tabs = [
    { id: 'products', label: 'Products', icon: '📦' },
    ...(editingProduct ? [{ id: 'edit', label: 'Edit Product', icon: '✏️' }] : []),
  ];

  useEffect(() => {
    fetchProducts();
    fetchModuleFields();
    fetchProjects();
  }, [currentPage]);

  // Filter products when category changes
  useEffect(() => {
    if (allProducts.length > 0) {
      if (selectedCategory) {
        const filtered = allProducts.filter(p => p.attributes?.category === selectedCategory);
        setProducts(filtered);
      } else {
        setProducts(allProducts);
      }
    }
  }, [selectedCategory, allProducts]);

  const fetchModuleFields = async () => {
    try {
      const response = await fetch(`${CHAT_API_BASE}/module-fields.php?module=AOS_Products`);
      const data = await response.json();
      if (data.success && data.fields) {
        setModuleFields(data.fields);
        const initialFormData = {};
        data.fields.forEach(field => {
          initialFormData[field.name] = '';
        });
        setFormData(initialFormData);
      }
    } catch (err) {
      console.error('Failed to fetch module fields:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${CHAT_API_BASE}/projects.php`);
      const data = await response.json();
      if (data.success && data.data) {
        setProjectOptions(data.data.map(p => ({ id: p.id, name: p.attributes?.name || p.name || 'Unnamed' })));
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use custom products API endpoint which joins with categories table
      const response = await fetch(`${CHAT_API_BASE}/products.php`);
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const data = await response.json();

      const productList = data.data || [];
      setAllProducts(productList);

      // Extract unique categories
      const uniqueCategories = [...new Set(
        productList
          .map(p => p.attributes?.category)
          .filter(c => c && c.trim())
      )].sort();
      setCategories(uniqueCategories);

      // Apply filter if one is selected
      if (selectedCategory) {
        const filtered = productList.filter(p => p.attributes?.category === selectedCategory);
        setProducts(filtered);
      } else {
        setProducts(productList);
      }

      if (response.meta && response.meta.total) {
        const total = Math.ceil(response.meta.total / 20);
        setTotalPages(total);
      }

      // Fetch captured images for each product
      const imagesMap = {};
      await Promise.all(productList.map(async (product) => {
        try {
          const imgResponse = await fetch(`${CHAT_API_BASE}/product-images.php?product_id=${product.id}`);
          if (imgResponse.ok) {
            const imgData = await imgResponse.json();
            if (imgData.success && imgData.images) {
              imagesMap[product.id] = imgData.images;
            }
          }
        } catch (e) {
          console.error(`Failed to fetch images for product ${product.id}:`, e);
        }
      }));
      setProductImages(imagesMap);

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setError('Failed to load products. Please try again.');
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const getImageUrl = (imagePath) => {
    return `/${imagePath}`;
  };

  const toggleProductExpand = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const deleteImage = async (imageId, productId, e) => {
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const response = await fetch(`${CHAT_API_BASE}/product-images.php?id=${imageId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setProductImages(prev => ({
          ...prev,
          [productId]: prev[productId].filter(img => img.id !== imageId)
        }));

        if (selectedImage && selectedImage.id === imageId) {
          setSelectedImage(null);
        }
      } else {
        alert('Failed to delete image');
      }
    } catch (err) {
      console.error('Failed to delete image:', err);
      alert('Failed to delete image');
    }
  };

  const handleDeleteClick = (e, product) => {
    e.stopPropagation();
    setShowDeleteConfirm(product);
  };

  const handleDeleteConfirm = async () => {
    if (!showDeleteConfirm) return;

    setDeleting(showDeleteConfirm.id);
    try {
      await api.deleteRecord('AOS_Products', showDeleteConfirm.id);
      setShowDeleteConfirm(null);
      await fetchProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
      alert('Failed to delete product. Please try again.');
    }
    setDeleting(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  // Edit handlers
  const handleEditClick = async (e, product) => {
    e.stopPropagation();
    setEditingProduct(product);
    const recordData = {};
    moduleFields.forEach(field => {
      recordData[field.name] = product.attributes?.[field.name] || '';
    });

    // Fetch project_id from custom table
    if (product.id) {
      try {
        const response = await fetch(`${CHAT_API_BASE}/record-project.php?module=AOS_Products&record_id=${product.id}`);
        const data = await response.json();
        if (data.success && data.data?.project_id) {
          recordData.project_id = data.data.project_id;
        }
      } catch (err) {
        console.error('Failed to fetch project_id:', err);
      }
    }

    setFormData(recordData);
    // Assemblies edit their component list (and rolled-up price/cost) in the
    // assembly builder rather than the plain field form.
    setActiveTab(Number(product.attributes?.is_assembly_c) === 1 ? 'assembly' : 'edit');
    setSaveMessage(null);
  };

  const handleNewProduct = () => {
    setEditingProduct(null);
    const emptyData = {};
    moduleFields.forEach(field => {
      emptyData[field.name] = '';
    });
    setFormData(emptyData);
    setActiveTab('edit');
    setSaveMessage(null);
  };

  const handleNewAssembly = () => {
    setEditingProduct(null);
    setSaveMessage(null);
    setActiveTab('assembly');
  };

  const handleAssemblySaved = async () => {
    await fetchProducts();
    setEditingProduct(null);
    setActiveTab('products');
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => {
      const next = { ...prev, [fieldName]: value };
      const cost = parseFloat(fieldName === 'cost' ? value : next.cost) || 0;
      const isBlank = (v) => v === '' || v === null || v === undefined;
      // Format helpers — round to 1dp for percent, 2dp for currency.
      const fmtPct = (n) => (Math.round(n * 10) / 10).toFixed(1);
      const fmtCur = (n) => (Math.round(n * 100) / 100).toFixed(2);

      if (fieldName === 'price') {
        // Price → derive Markup Amount and Markup Percent from Cost.
        if (isBlank(value)) {
          next.markup_amount_c = '';
          next.markup_percent_c = '';
        } else {
          const price = parseFloat(value) || 0;
          const amt = price - cost;
          next.markup_amount_c = fmtCur(amt);
          next.markup_percent_c = cost > 0 ? fmtPct((amt / cost) * 100) : '';
        }
      } else if (fieldName === 'markup_percent_c') {
        // Markup Percent → derive Markup Amount and Price.
        if (isBlank(value)) {
          next.markup_amount_c = '';
          // Leave price alone when percent is cleared
        } else {
          const pct = parseFloat(value) || 0;
          const amt = cost * pct / 100;
          next.markup_amount_c = fmtCur(amt);
          next.price = fmtCur(cost + amt);
        }
      } else if (fieldName === 'markup_amount_c') {
        // Markup Amount → derive Markup Percent (1dp) and Price.
        if (isBlank(value)) {
          next.markup_percent_c = '';
        } else {
          const amt = parseFloat(value) || 0;
          next.markup_percent_c = cost > 0 ? fmtPct((amt / cost) * 100) : '';
          next.price = fmtCur(cost + amt);
        }
      } else if (fieldName === 'cost') {
        // Cost changed: prefer the leg the user previously set —
        // markup_percent if it's set, else markup_amount, else hold price
        // and recompute the two markup fields from existing price.
        const pctSet = !isBlank(next.markup_percent_c);
        const amtSet = !isBlank(next.markup_amount_c);
        if (pctSet) {
          const pct = parseFloat(next.markup_percent_c) || 0;
          const amt = cost * pct / 100;
          next.markup_amount_c = fmtCur(amt);
          next.price = fmtCur(cost + amt);
        } else if (amtSet) {
          const amt = parseFloat(next.markup_amount_c) || 0;
          next.price = fmtCur(cost + amt);
          next.markup_percent_c = cost > 0 ? fmtPct((amt / cost) * 100) : '';
        } else if (!isBlank(next.price)) {
          const price = parseFloat(next.price) || 0;
          const amt = price - cost;
          next.markup_amount_c = fmtCur(amt);
          next.markup_percent_c = cost > 0 ? fmtPct((amt / cost) * 100) : '';
        }
      }

      return next;
    });
    if (editingProduct?.id) scheduleAutoSave();
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    const emptyData = {};
    moduleFields.forEach(field => {
      emptyData[field.name] = '';
    });
    setFormData(emptyData);
    setActiveTab('products');
    setSaveMessage(null);
  };

  // Auto-save (edit mode only): persist core fields via update, without navigation/project-linkage.
  const autoSaveProduct = async () => {
    if (!editingProduct?.id) return;
    const { project_id, ...rawFormData } = formData; // eslint-disable-line no-unused-vars
    const mainFormData = {};
    moduleFields.forEach(field => {
      if (field.name === 'project_id') return;
      const value = rawFormData[field.name];
      if (value === '' || value === undefined) {
        if (field.type === 'text' || field.type === 'textarea') mainFormData[field.name] = '';
      } else {
        mainFormData[field.name] = value;
      }
    });
    await api.updateRecord('AOS_Products', editingProduct.id, mainFormData);
  };
  const { schedule: scheduleAutoSave, status: autoStatus } = useAutoSave(autoSaveProduct);

  const handleSaveProduct = async () => {
    setSaving(true);
    setSaveMessage(null);

    try {
      const { project_id, ...rawFormData } = formData;
      let recordId = editingProduct?.id;

      const mainFormData = {};
      moduleFields.forEach(field => {
        if (field.name === 'project_id') return;
        const value = rawFormData[field.name];
        if (value === '' || value === undefined) {
          if (field.type === 'text' || field.type === 'textarea') {
            mainFormData[field.name] = '';
          }
        } else {
          mainFormData[field.name] = value;
        }
      });

      if (editingProduct) {
        await api.updateRecord('AOS_Products', editingProduct.id, mainFormData);
        setSaveMessage({ type: 'success', text: 'Product updated successfully!' });
      } else {
        const result = await api.createRecord('AOS_Products', mainFormData);
        recordId = result?.data?.id;
        setSaveMessage({ type: 'success', text: 'Product created successfully!' });
      }

      // Save project_id
      if (recordId) {
        try {
          await fetch(`${CHAT_API_BASE}/record-project.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              module: 'AOS_Products',
              record_id: recordId,
              project_id: project_id || null
            })
          });
        } catch (err) {
          console.error('Failed to save project_id:', err);
        }
      }

      await fetchProducts();

      setTimeout(() => {
        setActiveTab('products');
        setEditingProduct(null);
        const emptyData = {};
        moduleFields.forEach(field => {
          emptyData[field.name] = '';
        });
        setFormData(emptyData);
        setSaveMessage(null);
      }, 1500);

    } catch (err) {
      console.error('Failed to save product:', err);
      let errorMessage = 'Failed to save product. Please try again.';
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          errorMessage = errors.map(e => e.detail || e.title || e.message).join(', ');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setSaveMessage({ type: 'error', text: errorMessage });
    }

    setSaving(false);
  };

  const renderFormField = (field) => {
    const value = formData[field.name] || '';

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            className="form-textarea"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
            rows={4}
          />
        );

      case 'enum':
        return (
          <select
            className="form-select"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          >
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt || `Select ${field.label}`}</option>
            ))}
          </select>
        );

      case 'combobox':
        return (
          <>
            <input
              type="text"
              className="form-input"
              value={value}
              list={`${field.name}-options`}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={`Pick or type a ${field.label.toLowerCase()}`}
              autoComplete="off"
            />
            <datalist id={`${field.name}-options`}>
              {field.options?.map(opt => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          </>
        );

      case 'project_relate':
        return (
          <select
            className="form-select"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          >
            <option value="">Select {field.label}</option>
            {projectOptions.map(proj => (
              <option key={proj.id} value={proj.id}>{proj.name}</option>
            ))}
          </select>
        );

      case 'currency':
        return (
          <input
            type="number"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
            step="0.01"
            min="0"
          />
        );

      case 'url':
        return (
          <input
            type="url"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
          />
        );

      default:
        return (
          <input
            type="text"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
          />
        );
    }
  };

  return (
    <div className="products-page">
      <div className="products-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Dashboard
        </button>
        <div className="products-title-section">
          <HomeButton onClick={onBack} />
          <span className="products-icon-large">📦</span>
          <h1 className="products-title">Products</h1>
        </div>
        <div className="products-header-controls">
          <select
            className="products-category-filter"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <button className="refresh-button" onClick={fetchProducts} disabled={loading}>
            {loading ? '⟳' : '↻'} Refresh
          </button>
        </div>
      </div>

      <div className="products-content">
        <div className="vertical-tabs">
          <button className="vertical-tab new-record-tab" onClick={handleNewProduct}>
            <span className="tab-icon">➕</span>
            <span className="tab-label">New Product</span>
          </button>
          <button className="vertical-tab new-record-tab" onClick={handleNewAssembly}>
            <span className="tab-icon">🧩</span>
            <span className="tab-label">New Assembly</span>
          </button>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`vertical-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="tab-content">
        {activeTab === 'products' && (
          <>
        {loading && (
          <div className="products-loading">
            <div className="spinner"></div>
            <p>Loading products...</p>
          </div>
        )}

        {error && (
          <div className="products-error">
            <p>{error}</p>
            <button onClick={fetchProducts}>Try Again</button>
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <div className="products-empty">
            <span className="empty-icon">📦</span>
            <h3>No Products Found</h3>
            <p>{selectedCategory ? 'No products in this category.' : 'There are no products in the system yet.'}</p>
            <button className="create-first-btn" onClick={handleNewProduct}>
              Create First Product
            </button>
          </div>
        )}

        {!loading && !error && products.length > 0 && (
          <>
            <div className="products-grid">
              {products.map((product) => {
                const attrs = product.attributes || {};
                const images = productImages[product.id] || [];
                const isExpanded = expandedProduct === product.id;
                const mainImage = images.length > 0 ? images[0] : null;

                return (
                  <div
                    key={product.id}
                    className={`product-card clickable ${isExpanded ? 'expanded' : ''}`}
                    onClick={(e) => handleEditClick(e, product)}
                    title="Click to edit product"
                  >
                    <div className="product-card-image">
                      {mainImage ? (
                        <img
                          src={getImageUrl(mainImage.image_path)}
                          alt={attrs.name}
                          onClick={(e) => { e.stopPropagation(); setSelectedImage(mainImage); }}
                        />
                      ) : attrs.mainimage ? (
                        <img src={`/index.php?entryPoint=download&id=${attrs.mainimage}`} alt={attrs.name} />
                      ) : (
                        <div className="product-no-image">📦</div>
                      )}
                      {images.length > 1 && (
                        <span className="image-count" onClick={(e) => { e.stopPropagation(); toggleProductExpand(product.id); }}>
                          +{images.length - 1} more
                        </span>
                      )}
                    </div>
                    <div className="product-card-body">
                      <h3 className="product-name">{attrs.name || 'Unnamed Product'}</h3>
                      {attrs.client_facing_name_c && (
                        <p className="product-client-name">{attrs.client_facing_name_c}</p>
                      )}
                      <p className="product-description">{attrs.description || 'No description'}</p>
                      <div className="product-details">
                        <span className="product-price">{formatCurrency(attrs.price)}</span>
                        {attrs.part_number && (
                          <span className="product-sku">SKU: {attrs.part_number}</span>
                        )}
                      </div>
                      {attrs.category && (
                        <span className="product-type">{attrs.category}</span>
                      )}
                      <div className="product-card-actions">
                        <button
                          className="delete-product-btn"
                          onClick={(e) => handleDeleteClick(e, product)}
                          disabled={deleting === product.id}
                          title="Delete product"
                        >
                          {deleting === product.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>

                    {images.length > 0 && (
                      <div className="product-images-section">
                        <div
                          className="images-header"
                          onClick={(e) => { e.stopPropagation(); toggleProductExpand(product.id); }}
                        >
                          <span className="images-title">Captured Images ({images.length})</span>
                          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
                        </div>
                        {isExpanded && (
                          <div className="images-gallery">
                            {images.map((img) => (
                              <div
                                key={img.id}
                                className="gallery-image"
                                onClick={(e) => { e.stopPropagation(); setSelectedImage(img); }}
                              >
                                <img src={getImageUrl(img.image_path)} alt={img.image_name} />
                                <button
                                  className="gallery-image-delete"
                                  onClick={(e) => deleteImage(img.id, product.id, e)}
                                  title="Delete image"
                                >
                                  ×
                                </button>
                                <div className="gallery-image-info">
                                  <span className="gallery-image-source" title={img.source_url}>
                                    {img.source_url ? new URL(img.source_url).hostname : 'Unknown source'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
          </>
        )}

        {activeTab === 'edit' && (
          <div className="edit-form-container">
            <div className="edit-form-header">
              <h2>{editingProduct ? 'Edit Product' : 'Create New Product'}</h2>
              {editingProduct && (
                <span className="editing-id">ID: {editingProduct.id.slice(0, 8)}...</span>
              )}
            </div>

            {editingProduct && (() => {
              const imgs = productImages[editingProduct.id] || [];
              const main = imgs.length > 0 ? imgs[0] : null;
              const attrs = editingProduct.attributes || {};
              return (
                <div className="edit-product-image">
                  {main ? (
                    <img src={getImageUrl(main.image_path)} alt={attrs.name || 'Product'}
                         onClick={() => setSelectedImage(main)} title="Click to enlarge" />
                  ) : attrs.mainimage ? (
                    <img src={`/index.php?entryPoint=download&id=${attrs.mainimage}`} alt={attrs.name || 'Product'} />
                  ) : (
                    <div className="product-no-image">📦</div>
                  )}
                </div>
              );
            })()}

            {saveMessage && (
              <div className={`save-message ${saveMessage.type}`}>
                {saveMessage.text}
              </div>
            )}

            <div className="edit-form">
              {moduleFields.map((field, idx) => {
                const nextField = moduleFields[idx + 1];
                if (field.half_width && nextField?.half_width) {
                  return (
                    <div key={field.name} className="form-group-row">
                      <div className="form-group form-group-half">
                        <label className="form-label">{field.label}</label>
                        {renderFormField(field)}
                      </div>
                      <div className="form-group form-group-half">
                        <label className="form-label">{nextField.label}</label>
                        {renderFormField(nextField)}
                      </div>
                    </div>
                  );
                }
                if (field.half_width && idx > 0 && moduleFields[idx - 1]?.half_width) {
                  return null;
                }
                return (
                  <div
                    key={field.name}
                    className="form-group"
                  >
                    <label className="form-label">
                      {field.label}
                      {field.required && <span className="required">*</span>}
                    </label>
                    {renderFormField(field)}
                  </div>
                );
              })}
            </div>

            <div className="edit-form-actions">
              {editingProduct && (
                <span className={`form-autosave form-autosave-${autoStatus}`} style={{ marginRight: 'auto' }}>{autoSaveLabel(autoStatus)}</span>
              )}
              <button
                className="cancel-btn"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                {editingProduct ? 'Close' : 'Cancel'}
              </button>
              <button
                className="save-btn"
                onClick={handleSaveProduct}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingProduct ? 'Done' : 'Create Product')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'assembly' && (
          <ProductAssemblyForm
            products={allProducts}
            categories={categories}
            editingProduct={editingProduct}
            onCancel={() => { setEditingProduct(null); setActiveTab('products'); }}
            onSaved={handleAssemblySaved}
          />
        )}

        {selectedImage && (
          <div className="image-lightbox" onClick={() => setSelectedImage(null)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button className="lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
              <img src={getImageUrl(selectedImage.image_path)} alt={selectedImage.image_name} />
              <div className="lightbox-info">
                <p className="lightbox-name">{selectedImage.image_name}</p>
                {selectedImage.source_url && (
                  <a
                    href={selectedImage.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lightbox-source"
                  >
                    Source: {selectedImage.source_url}
                  </a>
                )}
                <p className="lightbox-dimensions">
                  {selectedImage.capture_width} × {selectedImage.capture_height}px
                </p>
                <button
                  className="lightbox-delete"
                  onClick={(e) => deleteImage(selectedImage.id, selectedImage.product_id, e)}
                >
                  Delete Image
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="delete-modal-overlay" onClick={handleDeleteCancel}>
            <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="delete-modal-header">
                <span className="delete-modal-icon">Warning</span>
                <h3>Delete Product?</h3>
              </div>
              <div className="delete-modal-body">
                <p>Are you sure you want to delete this product?</p>
                <p className="delete-product-name">
                  {showDeleteConfirm.attributes?.name || `Product #${showDeleteConfirm.id.slice(0, 8)}`}
                </p>
                <p className="delete-warning">This action cannot be undone.</p>
              </div>
              <div className="delete-modal-actions">
                <button
                  className="delete-modal-cancel"
                  onClick={handleDeleteCancel}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="delete-modal-confirm"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
